import React from 'react'
import { cn } from '../../utils/cn'
import { useThemeColors } from '../../hooks/useThemeColors'
import { useBranding } from '../../hooks/useBranding'

// Helper to convert hex to RGB string
function hexToRgbString(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '0, 242, 255'
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

// Helper to darken a hex color
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.max(0, (num >> 16) - amt)
  const G = Math.max(0, ((num >> 8) & 0x00FF) - amt)
  const B = Math.max(0, (num & 0x0000FF) - amt)
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'coachPrimary' | 'coachSecondary' | 'coach'
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  children: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    disabled,
    children,
    ...props
  }, ref) => {
    const { colors } = useThemeColors()
    const { branding } = useBranding()

    // Use org branding color or fallback to AIFM cyan
    const accentColor = branding?.primary_color || '#00f2ff'
    const accentColorDark = darkenColor(accentColor, 15)
    const accentRgb = hexToRgbString(accentColor)

    // Design System v2.0: 12px border radius, 48px min height, Space Grotesk font
    const baseStyles = 'inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'

    const getVariantStyles = () => {
      switch (variant) {
        case 'primary':
          // Design System v2.0: Gradient background using org branding, dark text, glow
          return {
            background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColorDark} 100%)`,
            color: '#080a0f',
            boxShadow: `0 0 15px rgba(${accentRgb}, 0.3)`,
            border: 'none',
            borderRadius: '12px',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover:not(:disabled)': {
              boxShadow: `0 0 20px rgba(${accentRgb}, 0.4)`,
              transform: 'translateY(-2px)'
            },
            '&:active:not(:disabled)': {
              transform: 'scale(0.98)'
            }
          }
        case 'secondary':
          // Design System v2.0: Transparent with org branding border
          return {
            backgroundColor: 'transparent',
            color: accentColor,
            border: `1px solid ${accentColor}`,
            borderRadius: '12px',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover:not(:disabled)': {
              backgroundColor: `rgba(${accentRgb}, 0.1)`,
              transform: 'translateY(-2px)',
              boxShadow: `0 0 15px rgba(${accentRgb}, 0.2)`
            }
          }
        case 'outline':
          return {
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover:not(:disabled)': {
              borderColor: accentColor,
              color: accentColor,
              backgroundColor: `rgba(${accentRgb}, 0.1)`,
              transform: 'translateY(-2px)'
            }
          }
        case 'ghost':
          return {
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            border: 'none',
            borderRadius: '12px',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover:not(:disabled)': {
              color: accentColor,
              backgroundColor: `rgba(${accentRgb}, 0.1)`,
              transform: 'translateY(-2px)'
            }
          }
        case 'danger':
          // Design System v2.0: Solid error color, no gradients
          return {
            backgroundColor: colors.error,
            color: '#080a0f',
            boxShadow: '0 0 15px rgba(239, 68, 68, 0.3)',
            border: 'none',
            borderRadius: '12px',
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover:not(:disabled)': {
              boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)',
              transform: 'translateY(-2px)'
            },
            '&:active:not(:disabled)': {
              transform: 'scale(0.98)'
            }
          }
        default:
          return {}
      }
    }

    // Design System v2.0: 48px minimum touch target compliance
    const sizes = {
      sm: 'px-4 py-2 text-sm min-h-[48px]',
      md: 'px-6 py-3 text-base min-h-[48px]',
      lg: 'px-8 py-4 text-lg min-h-[48px]',
      coachPrimary: 'h-[80px] min-h-[80px] px-8 text-lg font-semibold min-w-[200px]',
      coachSecondary: 'h-[56px] min-h-[56px] px-6 text-base',
      coach: 'h-[56px] min-h-[56px] px-6 text-base'
    }

    const variantStyles = getVariantStyles()

    return (
      <button
        className={cn(
          baseStyles,
          sizes[size],
          loading && 'cursor-wait',
          className
        )}
        style={{
          ...variantStyles,
          ...(props.style || {}),
          // Add focus ring styles using org branding
          '--tw-ring-color': accentColor,
          '--tw-ring-offset-color': '#080a0f'
        } as React.CSSProperties}
        disabled={disabled || loading}
        ref={ref}
        {...props}
        onMouseEnter={(e) => {
          if (!disabled && !loading && variantStyles['&:hover:not(:disabled)']) {
            Object.assign(e.currentTarget.style, variantStyles['&:hover:not(:disabled)'])
          }
          props.onMouseEnter?.(e)
        }}
        onMouseLeave={(e) => {
          if (!disabled && !loading) {
            Object.assign(e.currentTarget.style, variantStyles)
          }
          props.onMouseLeave?.(e)
        }}
        onMouseDown={(e) => {
          if (!disabled && !loading && variantStyles['&:active:not(:disabled)']) {
            Object.assign(e.currentTarget.style, {
              ...variantStyles['&:hover:not(:disabled)'],
              ...variantStyles['&:active:not(:disabled)']
            })
          }
          props.onMouseDown?.(e)
        }}
        onMouseUp={(e) => {
          if (!disabled && !loading && variantStyles['&:hover:not(:disabled)']) {
            Object.assign(e.currentTarget.style, variantStyles['&:hover:not(:disabled)'])
          }
          props.onMouseUp?.(e)
        }}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            Loading...
          </>
        ) : (
          <>
            {leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2">{rightIcon}</span>}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
